import { errorMessages } from '@readcloud/common';
import { AppModulePermission } from '@readcloud/common/types/models/api/DTO';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import type {
  PDFFindController,
  PDFLinkService,
} from 'pdfjs-dist/web/pdf_viewer';
import { TouchEvent } from 'react';
import { Subject } from 'rxjs';

export interface TOCItem {
  title: string;
  subtitle?: string;
  dest: any;
  items?: TOCItem[];
  url?: string | null;
  newWindow?: boolean;
}

export type PermissionModule = AppModulePermission;

export type NativeRenderingResolution = 'Exact' | '* DPR' | '* DPR * 96/72';
export interface PageLabel {
  value: string;
  label: string;
}
export type PageLabelList = PageLabel[];
export interface ErrorType {
  type: keyof typeof errorMessages;
  error?: Error;
}

type DocumentControlActionType =
  | 'next'
  | 'prev'
  | 'changeLocation'
  | 'gotoToc'
  | 'customTextSelectionUpdate';
type AnnotationControlActionType = 'scrollTo';

export interface DocumentControlAction<Type extends DocumentControlActionType> {
  type: Type;
}
export interface DocumentActionWithPayload<
  Type extends DocumentControlActionType,
  Payload
> {
  type: Type;
  payload: Payload;
  debounce?: boolean;
}

interface AnnotationActionWithPayload<
  Type extends AnnotationControlActionType,
  Payload
> {
  type: Type;
  payload: Payload;
  debounce?: boolean;
}

export type DocumentControlActions =
  | DocumentControlAction<'next'>
  | DocumentControlAction<'prev'>
  | DocumentActionWithPayload<'changeLocation', { location: string }>
  | DocumentActionWithPayload<'gotoToc', TOCItem>
  | DocumentControlAction<'customTextSelectionUpdate'>;

export type AnnotationListControlActions = AnnotationActionWithPayload<
  'scrollTo',
  { location: string }
>;

export interface Dimensions {
  height: number;
  width: number;
}

export interface Viewport {
  height: number;
  width: number;
  scale: number;
}

export interface PDFViewerImplementationProps {
  pdfDocument: PDFDocumentProxy;
  onTouchMove?: (e: TouchEvent<any>) => void;
  containerRef?: (el: HTMLDivElement) => void;
  height: number;
  width: number;
  findController?: PDFFindController;
  linkService?: PDFLinkService;
  onBackgroundClick: () => void;
  minScale: number;
  maxScale: number;
  pagesPerView: number;
  backgroundColor?: string;
  minPagePadding: number;
  unitViewport: Viewport;
  redrawDebounceTime: number;
  updateDebounceTime: number;
  rateLimitTime?: number;
  bufferSize: number;
  control: Subject<DocumentControlActions>;
  onScaleChange?: (scale: number) => void;
  scale: number;
  onPageChange?: (pageNumber: number) => void;
  pageCount: number;
  renderPage: (p: {
    onPageDraw: (page: { id: number; scale: number }) => void;
    onPageUndraw: (page: { id: number; scale: number }) => void;
    pageNumber: number;
    height: number;
    width: number;
    paused: boolean;
    style?: Partial<React.CSSProperties>;
    findController: any;
    ref: (el: HTMLElement) => void;
    key: any;
    isVisible: boolean;
    externalOverflownContainer?: HTMLElement;
  }) => React.ReactNode;
  lockScroll?: boolean;
  initialPageNumber?: number;
  overscanCount?: number;
  vertical?: boolean;
  animationsDisabled: boolean;
  singlePage?: number;
}

export type PDFViewerImplementation =
  React.ComponentType<PDFViewerImplementationProps>;

export type NativeRenderingEngine = 'PdfRenderer' | 'Pdfium';

interface RendererParams {
  textLayerId?: string;
  textLayer: HTMLElement;
  pageNumber: number;
  contentLayer: HTMLElement;
  height: number;
  width: number;
  scale: number;
}

export interface PDFPageProps {
  unlockScroll: () => void;
  lockScroll?: boolean;
  disableNativeTextSelection: boolean;
  innerRef?: (el: HTMLDivElement) => void;
  className?: string;
  pageClass?: string;
  renderHighlightsLayer: (params: RendererParams) => React.ReactNode;
  renderShapesLayer?: (params: RendererParams) => React.ReactNode;
  renderLearningToolbar: (params: RendererParams) => React.ReactNode;
  findController: PDFFindController;
  style?: React.CSSProperties;
  zIndex: number;
  textLayerZIndex: number;
  highlightsLayerZIndex: number;
  midLayerZIndex: number;
  learningToolbarZIndex: number;
  document: PDFDocumentProxy;
  pageNumber: number;
  height: number;
  width: number;
  firstPage: PDFPageProxy;
  paused: boolean;
  destroyed?: boolean;
  onTextLayerRendered?: (textLayer: HTMLElement) => void;
  onPageDraw?: (component: any) => void;
  onPageUndraw: (component: any) => void;
  onRenderedChange?: (rendered: boolean) => void;
  onSize?: (size: {
    height: number;
    width: number;
    actualScale: number;
  }) => void;
  maxCanvasPixels?: number;
  hideSearchResults?: boolean;
  isVisible: boolean;
  externalOverflownContainer?: HTMLElement; // Use this for rendering learning toolbar and annotation popovers to. Make sure overflown content is going to be visible within it (eg if popovers are too big for the page)
}

export interface FindParams {
  highlightAll: boolean;
  caseSensitive: boolean;
  query?: string;
  phraseSearch?: boolean;
}
export interface FindController {
  totalMatchCount: number;
  currentMatchCount: number;
  findState: number;
  find: (params: FindParams) => void;
  findPrevious: () => void;
  findNext: () => void;
}

export type ScaleAction = 'page-width';

export type WalkthroughVersion = 1;

export type PrintRights = {
  printablePages: number[];
  printableRanges: string;
};

export const scaleModes = {
  '100': 1,
  '125': 1.25,
  '150': 1.5,
  '175': 1.75,
  '200': 2,
  '250': 2.5,
  '300': 3,
  '400': 4,
};

export const maxScale = 4;
export const minScale = 1;
export const scaleStep = 0.1;
